import { useClipboard } from '@mantine/hooks'
import { useGenericFormStore } from '@neo/blocks/forms/persistance-store'
import { Button } from '@neo/primitives/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@neo/primitives/dropdown-menu'
import { ChevronDown } from 'lucide-react'
import { useParams } from 'router-v6'
import { isProd } from 'v2/configs/constants'
import { queryClient } from 'v2/configs/rq-setup'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { FlowTypes } from 'v2/types'
import { errorToast } from 'v2/utils/toasts'
import { AGENT_FLOW_API_URL } from '../constants'
import { useStartNodeAuthData } from '../hooks/use-start-node-auth-data'
import { useStartNodeType } from '../hooks/use-start-node-type'
import { useFlowStore } from '../store'
import { type FlowData, FlowNodeTypes } from '../types'
import { isSubaccountTagUnsaved } from '../utils'
import { type CodeLanguage, type FormValues, generateCode } from './code-generators'

const FORM_PERSISTENCE_KEY = 'api-request-payload-form'

// Shown while a staged tag change holds copying — as the tooltip on the disabled
// buttons and as the caption under them.
const TAG_CHANGE_UNSAVED_HINT = 'Save to copy with the new sub account'

interface CopyAgentTriggerProps {
  selectedNodeType?: string
}

const languageOptions: { value: CodeLanguage; label: string }[] = [
  { value: 'python', label: 'Python' },
  { value: 'node', label: 'Node.js' },
  { value: 'go', label: 'Go' },
  { value: 'ruby', label: 'Ruby' },
  { value: 'php', label: 'PHP' },
  { value: 'java', label: 'Java' },
  { value: 'dotnet', label: '.NET' },
]

export const CopyAgentTrigger = ({ selectedNodeType }: CopyAgentTriggerProps) => {
  const type = useStartNodeType()
  const { id: flowId } = useParams()
  // A tagged flow copies code that authenticates as the SUBACCOUNT it
  // runs under (id + token), not the main account — see the hook for why.
  const { authData } = useStartNodeAuthData(flowId, type)
  const clipboard = useClipboard({ timeout: 2000 })
  // The trigger URL is subaccount-scoped and comes from the SAVED tag
  // (config-service builds it as phlo_url(sub_auth_id or org auth_id)), while
  // the credentials above follow the STAGED tag. While the two disagree the
  // snippet would pair one account's key with another's URL, so copying is
  // held until the save reconciles them.
  const agentSettings = useFlowStore((state) => state.agentSettings)
  const initialAgentSettings = useFlowStore((state) => state.initialAgentSettings)
  const tagChangeUnsaved = isSubaccountTagUnsaved(agentSettings, initialAgentSettings)

  const isStartNode = selectedNodeType === FlowNodeTypes.start

  if (!isStartNode || type !== FlowTypes.api_request || !flowId || !authData?.authId) {
    return null
  }
  // The token is withheld for sessions without `api_credentials view`; the snippet then carries
  // the credential placeholder instead of disappearing, same as the Start node's locked field.
  const { authId, authToken = '' } = authData

  const handleCopy = (language: CodeLanguage) => {
    // Both controls are disabled in this state; this only stops a stray path
    // from emitting a snippet whose credentials and URL point at different accounts.
    if (tagChangeUnsaved) return
    try {
      const flowData = queryClient.getQueryData<FlowData>(flowQueryKeys().flowData(flowId))
      const startNodeConfig = flowData?.nodes?.find((n) => n.type === 'start')?.data?.config
      const payloadFormat = startNodeConfig?.payload_format

      // Get current form state for require_http_auth (respects unsaved toggle changes)
      const sidebarFormInstance = useFlowStore.getState().sidebarFormInstance
      const requireHttpAuth = sidebarFormInstance?.state?.values?.require_http_auth ?? true

      const formValues = useGenericFormStore.getState().formValues[FORM_PERSISTENCE_KEY] as
        | FormValues
        | undefined

      const baseUrl = isProd ? AGENT_FLOW_API_URL.prod : AGENT_FLOW_API_URL.dev
      // authId is the tagged flow's sub_auth_id (or the MA when untagged) —
      // see useStartNodeAuthData.
      const url = flowData?.phlo?.url || `${baseUrl}/v1/account/${authId}/flow/${flowId}`

      const code = generateCode(
        language,
        url,
        authId,
        authToken,
        payloadFormat as Record<string, unknown> | string,
        formValues,
        requireHttpAuth,
      )

      clipboard.copy(code)
    } catch (e) {
      errorToast({ title: 'Failed to copy', description: `${e}` })
    }
  }

  return (
    <div className="flex flex-col items-end gap-1 enable-in-view-mode">
      <div className="flex items-center gap-0">
        <Button
          variant="outline"
          size="xs"
          type="button"
          data-testid="button-copy-curl"
          disabled={tagChangeUnsaved}
          disabledReason={tagChangeUnsaved ? TAG_CHANGE_UNSAVED_HINT : undefined}
          onClick={() => handleCopy('curl')}
          className="text-secondary rounded-r-none border-r-0"
        >
          {clipboard.copied ? 'Copied!' : 'Copy cURL'}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={tagChangeUnsaved}>
            <Button
              variant="outline"
              size="xs"
              type="button"
              data-testid="button-copy-dropdown"
              disabled={tagChangeUnsaved}
              disabledReason={tagChangeUnsaved ? TAG_CHANGE_UNSAVED_HINT : undefined}
              className="text-secondary rounded-l-none px-1.5"
            >
              <ChevronDown size={14} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {languageOptions.map((option) => (
              <DropdownMenuItem
                key={option.value}
                onClick={() => handleCopy(option.value)}
                data-testid={`menuitem-copy-${option.value}`}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {tagChangeUnsaved && (
        <span className="text-s-400 text-secondary" data-testid="text-copy-tag-unsaved">
          {TAG_CHANGE_UNSAVED_HINT}
        </span>
      )}
    </div>
  )
}
